import { c as _c } from "react/compiler-runtime";
import React, { useEffect, useId, useRef, useState } from "react";

/*
 * Apple-style form controls (current iOS / apple.com): soft gray filled
 * capsules (~50px tall, textareas 24px corners) with no border, the label sitting inside
 * and floating up once the field is focused or filled; on focus the field
 * turns white with a blue outline and glow; errors tint it red.
 */

/** The bare control inside a Field: no chrome of its own, the Field box draws it. */
export const INPUT = "block w-full bg-transparent px-6 pt-[21px] pb-[5px] text-[16px] leading-[20px] tracking-[-0.016em] text-[var(--foreground)] outline-none rounded-full";
export const TEXTAREA = "block w-full bg-transparent px-6 pt-[26px] pb-3 text-[16px] leading-[22px] tracking-[-0.016em] text-[var(--foreground)] outline-none rounded-[24px] min-h-[132px] resize-y";

/** Selects get their own chevron (see Field's `chevron`). */
export const SELECT = `${INPUT} appearance-none pe-12 cursor-pointer`;

/** apple.com's filled search field. */
export const SEARCH = "w-full rounded-full bg-[#F2F2F7] dark:bg-[#2C2C2E] ps-11 pe-5 py-2.5 text-[16px] tracking-[-0.022em] text-[var(--foreground)] placeholder:text-[#86868B] outline-none focus:ring-4 focus:ring-[#0071E3]/20";
export function Field(t0) {
  const $ = _c(25);
  const {
    label,
    hint,
    error,
    optional,
    optionalText,
    filled,
    multiline,
    chevron,
    dir,
    className: t1,
    children
  } = t0;
  const className = t1 === undefined ? "" : t1;
  const resting = multiline ? "top-[15px]" : "top-1/2 -translate-y-1/2";
  let t2;
  if ($[0] !== filled || $[1] !== resting) {
    t2 = filled ? "top-[6px] translate-y-0 text-[11px] leading-[14px]" : [resting, "text-[16px] leading-[20px] group-focus-within:top-[6px] group-focus-within:translate-y-0 group-focus-within:text-[11px] group-focus-within:leading-[14px]"].join(" ");
    $[0] = filled;
    $[1] = resting;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const labelPosition = t2;
  const t3 = `group relative block ${multiline ? "rounded-[24px]" : "rounded-full"} border-[1.5px] transition-[border-color,box-shadow,background-color] duration-200 ${error ? "border-[#E30000] bg-[#FFF2F4] dark:bg-[#3A1D1D] focus-within:shadow-[0_0_0_4px_rgba(227,0,0,0.12)]" : "border-transparent bg-[#F2F2F7] dark:bg-[#2C2C2E] hover:bg-[#EBEBF0] dark:hover:bg-[#3A3A3C] focus-within:bg-white dark:focus-within:bg-[#1C1C1E] focus-within:border-[#0071E3] focus-within:shadow-[0_0_0_4px_rgba(0,113,227,0.15)]"}`;
  const t4 = `pointer-events-none absolute start-6 end-6 truncate tracking-[-0.01em] transition-all duration-200 ${error ? "text-[#E30000]" : "text-[#86868B]"} ${labelPosition}`;
  let t5;
  if ($[3] !== optional || $[4] !== optionalText) {
    t5 = optional && optionalText && <span className="opacity-70"> ({optionalText})</span>;
    $[3] = optional;
    $[4] = optionalText;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] !== label || $[7] !== t4 || $[8] !== t5) {
    t6 = <span className={t4}>{label}{t5}</span>;
    $[6] = label;
    $[7] = t4;
    $[8] = t5;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== chevron) {
    t7 = chevron && <svg className="pointer-events-none absolute end-6 top-1/2 -translate-y-1/2 w-4 h-4 text-[#86868B]" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24" aria-hidden={true}><path strokeLinecap="round" strokeLinejoin="round" d="M6 9l6 6 6-6" /></svg>;
    $[10] = chevron;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== children || $[13] !== dir || $[14] !== t3 || $[15] !== t6 || $[16] !== t7) {
    t8 = <label dir={dir} className={t3}>{t6}{children}{t7}</label>;
    $[12] = children;
    $[13] = dir;
    $[14] = t3;
    $[15] = t6;
    $[16] = t7;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[18] !== error || $[19] !== hint) {
    t9 = <FieldMessage error={error} hint={hint} />;
    $[18] = error;
    $[19] = hint;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  let t10;
  if ($[21] !== className || $[22] !== t8 || $[23] !== t9) {
    t10 = <div className={className}>{t8}{t9}</div>;
    $[21] = className;
    $[22] = t8;
    $[23] = t9;
    $[24] = t10;
  } else {
    t10 = $[24];
  }
  return t10;
}

/** Error (with Apple's exclamation mark) or hint under a field or group. */
export function FieldMessage(t0) {
  const $ = _c(5);
  const {
    error,
    hint
  } = t0;
  if (error) {
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <svg className="w-3.5 h-3.5 shrink-0" viewBox="0 0 16 16" fill="currentColor" aria-hidden={true}><path d="M8 1a7 7 0 100 14A7 7 0 008 1zm-.75 3.5h1.5v5h-1.5v-5zM8 12.25a.9.9 0 110-1.8.9.9 0 010 1.8z" /></svg>;
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    let t2;
    if ($[1] !== error) {
      t2 = <p role="alert" className="mt-2 flex items-center gap-1.5 px-1 text-[12px] leading-[16px] text-[#E30000]">{t1}{error}</p>;
      $[1] = error;
      $[2] = t2;
    } else {
      t2 = $[2];
    }
    return t2;
  }
  let t1;
  if ($[3] !== hint) {
    t1 = hint ? <p className="mt-2 px-1 text-[12px] leading-[16px] text-[#86868B] text-justify">{hint}</p> : null;
    $[3] = hint;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  return t1;
}

/** Selectable option: filled gray capsule, blue outline and tint once chosen. */
export function Chip(t0) {
  const $ = _c(10);
  const {
    selected,
    onClick,
    children,
    tile: t1,
    badge,
    icon
  } = t0;
  const tile = t1 === undefined ? false : t1;
  const shape = tile ? "grow basis-auto min-h-9 flex items-center justify-center gap-1.5 rounded-full px-3 sm:px-4 py-1.5 sm:py-2 text-[13px] sm:text-[14px] text-center leading-tight" : `px-4 py-2 sm:px-5 sm:py-2.5 rounded-full text-[14px] sm:text-[15px] ${icon ? "inline-flex items-center gap-2" : ""}`;
  const t2 = `${shape} border-[1.5px] tracking-[-0.016em] transition-all duration-200 cursor-pointer focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-[#0071E3]/20 ${selected ? "border-[#0071E3] bg-[#0071E3]/[0.08] text-[#0071E3] dark:text-[#2997FF]" : "border-transparent bg-[#F2F2F7] dark:bg-[#2C2C2E] text-[var(--foreground)] hover:bg-[#EBEBF0] dark:hover:bg-[#3A3A3C]"}`;
  let t3;
  if ($[0] !== badge || $[1] !== selected) {
    t3 = badge != null && selected && <span className="shrink-0 inline-flex items-center justify-center w-4 h-4 sm:w-[18px] sm:h-[18px] rounded-full bg-[#0071E3] text-white text-[10px] font-semibold leading-none" aria-hidden={true}>{badge}</span>;
    $[0] = badge;
    $[1] = selected;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== children || $[4] !== icon || $[5] !== onClick || $[6] !== selected || $[7] !== t2 || $[8] !== t3) {
    t4 = <button type="button" aria-pressed={selected} onClick={onClick} className={t2}>{icon}{t3}{children}</button>;
    $[3] = children;
    $[4] = icon;
    $[5] = onClick;
    $[6] = selected;
    $[7] = t2;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  return t4;
}

/**
 * Chips that fill every row: each starts at its label's width, so a row holds
 * as many as fit (two, three, four...), then they grow to reach the edge — no
 * ragged right side and no hole after the last one.
 */
export const TILE_GRID = "flex flex-wrap gap-2";

/** Legend for a group of chips, styled like a field label. */
export function GroupLegend(t0) {
  const $ = _c(2);
  const {
    children
  } = t0;
  let t1;
  if ($[0] !== children) {
    t1 = <legend className="mb-3 text-[15px] sm:text-[17px] tracking-[-0.022em] text-[var(--foreground)]">{children}</legend>;
    $[0] = children;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
}
export function StepTitle(t0) {
  const $ = _c(6);
  const {
    title,
    subtitle
  } = t0;
  let t1;
  if ($[0] !== subtitle || $[1] !== title) {
    t1 = subtitle && <InfoTip label={title}>{subtitle}</InfoTip>;
    $[0] = subtitle;
    $[1] = title;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== t1 || $[4] !== title) {
    t2 = <header className="relative mb-5 sm:mb-6"><h2 className="flex items-center gap-1.5 text-[18px] sm:text-[21px] font-semibold leading-[1.19] tracking-[0.011em] text-[var(--foreground)]">{title}{t1}</h2></header>;
    $[3] = t1;
    $[4] = title;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  return t2;
}

/**
 * An (i) button that opens a small bubble with extra explanation, so step
 * intros don't push the form down on a phone. Closes on outside tap or Escape.
 */
export function InfoTip(t0) {
  const $ = _c(17);
  const {
    label,
    children
  } = t0;
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const id = useId();
  let t1;
  let t2;
  if ($[0] !== open) {
    t1 = () => {
      if (!open) {
        return;
      }
      const onPointer = e => ref.current && !ref.current.contains(e.target as Node) && setOpen(false);
      const onKey = e_0 => e_0.key === "Escape" && setOpen(false);
      document.addEventListener("pointerdown", onPointer);
      document.addEventListener("keydown", onKey);
      return () => {
        document.removeEventListener("pointerdown", onPointer);
        document.removeEventListener("keydown", onKey);
      };
    };
    t2 = [open];
    $[0] = open;
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  useEffect(t1, t2);
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => setOpen(_temp);
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const t4 = `inline-flex items-center justify-center w-7 h-7 -my-1 rounded-full cursor-pointer transition-colors ${open ? "text-[#0071E3]" : "text-[#86868B] hover:text-[#0071E3]"}`;
  let t5;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <svg className="w-[18px] h-[18px]" fill="none" stroke="currentColor" strokeWidth={1.8} viewBox="0 0 24 24" aria-hidden={true}><circle cx="12" cy="12" r="9" /><path strokeLinecap="round" d="M12 11v5" /><circle cx="12" cy="7.75" r="0.6" fill="currentColor" /></svg>;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  let t6;
  if ($[5] !== id || $[6] !== label || $[7] !== open || $[8] !== t4) {
    t6 = <button type="button" onClick={t3} aria-expanded={open} aria-controls={id} aria-label={label} className={t4}>{t5}</button>;
    $[5] = id;
    $[6] = label;
    $[7] = open;
    $[8] = t4;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== children || $[11] !== id || $[12] !== open) {
    t7 = open && <span id={id} role="tooltip" className="absolute z-20 top-full mt-2 inset-x-0 sm:end-auto sm:w-[22rem] rounded-2xl bg-white dark:bg-[#2C2C2E] ring-1 ring-black/[0.06] dark:ring-white/[0.08] shadow-[0_12px_32px_-8px_rgba(0,0,0,0.25)] px-4 py-3 text-[13px] sm:text-[14px] leading-[1.45] tracking-[-0.01em] text-[var(--foreground-secondary)] text-justify hyphens-auto">{children}</span>;
    $[10] = children;
    $[11] = id;
    $[12] = open;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== t6 || $[15] !== t7) {
    t8 = <span ref={ref} className="inline-flex font-normal">{t6}{t7}</span>;
    $[14] = t6;
    $[15] = t7;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  return t8;
}
function _temp(v) {
  return !v;
}